import { useParams, Link } from 'react-router-dom';
import { useProject } from '../hooks/useProjects';
import GlobalHeader from '../components/layout/GlobalHeader';
import { useScrollY } from '../hooks/useScrollY';
import { useLenis } from '../hooks/useLenis';
import YouTubeEmbed from '../components/ui/YouTubeEmbed';
import { renderFormattedText, sectionTextStyle } from '../utils/richText';

export default function ProjectDetailPage() {
  const { id }               = useParams();
  useLenis();
  const scrollY              = useScrollY();
  const { project, loading } = useProject(id);

  if (loading) {
    return (
      <>
        <GlobalHeader hamburgerOnly heroMode />
        <div className="not-found-wrap">
          <div style={{ color: '#a3a3a3', fontSize: '0.875rem' }}>Loading...</div>
        </div>
      </>
    );
  }

  if (!project) {
    return (
      <>
        <GlobalHeader hamburgerOnly heroMode />
        <div className="not-found-wrap">
          <div className="not-found-inner">
            <p className="not-found-msg">Project not found</p>
            <Link to="/work" className="btn-primary">Back to Work</Link>
          </div>
        </div>
      </>
    );
  }

  const heroImage = project.images?.[0]?.src ?? null;
  const category  = project.type === 'uiux' ? 'UI/UX'
                  : project.type ? project.type.charAt(0).toUpperCase() + project.type.slice(1)
                  : '';
  const tags      = project.tags || [];
  const desc      = project.shortDescription || '';

  const vh          = typeof window !== 'undefined' ? window.innerHeight : 800;
  const scrollable  = typeof window !== 'undefined' ? document.documentElement.scrollHeight - vh : 1;
  const progress    = scrollable > 0 ? Math.min(1, scrollY / scrollable) : 0;
  const barVisible  = scrollY > vh * 0.5;
  const bodyRadius  = Math.round(Math.max(0, 20 * (1 - Math.min(scrollY / (vh * 0.5), 1))));

  const metaRows = [
    { label: '제목',     value: project.title },
    { label: '작업 기간', value: project.workPeriod },
    { label: '작업성격', value: project.workType },
    { label: '롤',       value: project.role },
    { label: '기여도',   value: project.contribution },
    { label: '작업 툴',  value: project.tools },
    { label: '링크',     value: project.linkUrl, href: project.linkUrl },
  ].filter((r) => r.value);

  return (
    <>
      <GlobalHeader hamburgerOnly heroMode />

      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <section className="ld-hero" style={{ background: project.coverColor || '#0a0a0a' }}>
        {heroImage && (
          <div className="ld-hero__bg" style={{ backgroundImage: `url(${heroImage})` }} />
        )}
        <div className="ld-hero__gradient" />
        <div className="ld-hero__foot">
          <div className="ld-wrap">
            <div className="ld-meta-row">
              {project.year && <span className="ld-meta-text">{project.year}</span>}
              {project.year && category && <span className="ld-meta-sep">·</span>}
              {category && <span className="ld-meta-text">{category}</span>}
            </div>
            <h1 className="ld-title">{project.title}</h1>
            {tags.length > 0 && (
              <div className="ld-tags">
                {tags.map(t => <span key={t} className="ld-tag">{t}</span>)}
              </div>
            )}
            {desc && <p className="ld-desc">{desc}</p>}
          </div>
        </div>
      </section>

      {/* ── Body ─────────────────────────────────────────────────────── */}
      <div className="ld-body" style={{ borderRadius: `${bodyRadius}px ${bodyRadius}px 0 0` }}>
        <div className="ld-wrap">
          {metaRows.length > 0 && (
            <div className="ld-meta-box">
              {metaRows.map((r) => (
                <div key={r.label} className="ld-meta-box__row">
                  <span className="ld-meta-box__label">{r.label}</span>
                  {r.href ? (
                    <a href={r.href} target="_blank" rel="noopener noreferrer" className="ld-meta-box__value ld-meta-box__link">{r.value}</a>
                  ) : (
                    <span className="ld-meta-box__value">{r.value}</span>
                  )}
                </div>
              ))}
            </div>
          )}
          {(project.sections || []).map((sec, i) => (
            <div key={i} className="ld-section">
              <span className="ld-section__label" style={sectionTextStyle(sec)}>{sec.title}</span>
              {sec.content && (
                <p className="ld-section__text" style={sectionTextStyle(sec)}>{renderFormattedText(sec.content)}</p>
              )}
              {sec.videoUrl && <YouTubeEmbed url={sec.videoUrl} />}
              {sec.images && sec.images.length > 0 && (
                <div className="ld-section__imgs">
                  {sec.images.map((img, j) => (
                    <img
                      key={j}
                      src={img.src || img.url || img}
                      alt={img.alt || sec.title}
                      className="ld-section__img"
                      loading="lazy"
                    />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* ── Bottom bar (fixed progress bar) ─────────────────────────── */}
      <div className={`ld-bottom${barVisible ? ' ld-bottom--visible' : ''}`}>
        <div className="ld-progress" style={{ width: `${progress * 100}%` }} />
        <div className="ld-wrap ld-bottom__inner">
          <p className="ld-bottom__title">{project.title}</p>
          <Link to="/work" className="ld-back-btn">← All Work</Link>
        </div>
      </div>

      <Link to="/contact" className="float-contact float-contact--raised" aria-label="Contact">
        Contact
      </Link>
    </>
  );
}
